/**
 * Page partagée pour les services événementiels privés :
 *  - /services/evjf            (Enterrement de Vie de Jeune Fille / EVJG)
 *  - /services/soiree-privee   (anniversaire, mariage, soirée Pleine/Nouvelle Lune)
 *
 * Mêmes prestations sous-jacentes (Corner de Voyance, Cercle de Lune, Combo)
 * mais H1, meta, JSON-LD et angle éditorial dédiés à chaque intention de recherche
 * pour maximiser la visibilité SEO.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles, Moon, Clock, Users, MapPin, ChevronDown, Check, Heart,
  ShieldCheck, Calendar, Wand2, Mail, ArrowRight,
} from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";

// Helmet via App.js export — on duplique le composant local pour éviter import circulaire
import { Helmet } from "react-helmet-async";
const SITE_URL = process.env.REACT_APP_SITE_URL || "https://www.bilan2vie.fr";

const FORMULAS = [
  {
    icon: Sparkles,
    title: "Corner de Voyance 🔮",
    desc: "Tirages individuels de 7 à 15 min — amour, pro, mission de vie, prochaine étape. Idéal pendant un cocktail, un vin d'honneur ou entre deux playlists.",
    color: "#D926A9",
  },
  {
    icon: Moon,
    title: "Cercle de Lune 🌙",
    desc: "Ouverture de l'espace, intentions (Nouvelle Lune) ou libérations (Pleine Lune), guidance collective + 1 carte par personne si souhaité.",
    color: "#00E5FF",
  },
  {
    icon: Wand2,
    title: "Combo Lune + Voyance ✨",
    desc: "45 min de rituel + créneaux de tirages express — le meilleur des deux mondes pour un effet wow garanti.",
    color: "#9D4CDD",
  },
];

const TIMELINE = [
  { step: "1", title: "Brief", desc: "Thème, timing, contraintes — on cale tout ensemble." },
  { step: "2", title: "Préparation sur-mesure", desc: "Trame, jeux et rituels ajustés à la Lune du moment." },
  { step: "3", title: "Jour J", desc: "Installation rapide, rituel ou mini-lectures, restitutions claires et utiles." },
  { step: "4", title: "Clôture", desc: "Ancrage, mantra, photo souvenir si souhaitée." },
];

const VALUE_PROPS = [
  {
    icon: Heart,
    title: "Guidance éthique & claire",
    desc: "Humour fin quand il détend, profondeur quand elle élève. Toujours bienveillant, jamais alarmiste.",
  },
  {
    icon: ArrowRight,
    title: "Messages orientés solutions",
    desc: "Petits pas concrets, rituels simples, idées de journaling à emporter chez soi.",
  },
  {
    icon: Sparkles,
    title: "Esthétique travaillée",
    desc: "Ambiance mystique chic par défaut, ou autre univers sur demande pour matcher ton thème.",
  },
];

const COMMON_FAQ_BASE = [
  {
    q: "Combien de personnes peuvent participer ?",
    a: "La formule de base couvre jusqu'à 10 personnes pour 70€/heure. Au-delà, on allonge la durée (format bar à tirages) ou on ouvre un 2ᵉ créneau — devis personnalisé.",
  },
  {
    q: "Quelle durée prévoir ?",
    a: "Entre 2 et 3 heures selon le format choisi. Le Combo Lune + Voyance fonctionne très bien sur 2h30 (45 min de rituel collectif + 2h de tirages individuels).",
  },
  {
    q: "Quel matériel dois-je prévoir ?",
    a: "Rien ! J'apporte tarots, oracles, tapis, bougies LED et brume énergétique douce. Tu prévois juste une table + 2 chaises (pour le Corner) ou un espace calme (pour le Cercle).",
  },
  {
    q: "Tu te déplaces dans quelle zone ?",
    a: "Je me déplace dans un rayon de 50 km autour de Montélimar (26). Ça couvre Valence, Pierrelatte, Bourg-Saint-Andéol, Bollène, Orange, Privas, Aubenas et toute la Drôme provençale. Frais de déplacement précisés au devis selon le lieu exact.",
  },
  {
    q: "Faut-il verser des arrhes ?",
    a: "Oui : 60€ d'arrhes à la réservation (déduites du total). Le solde se règle le jour J au plus tard.",
  },
  {
    q: "Que se passe-t-il en cas d'annulation ?",
    a: "Annulation ou modification sans frais jusqu'à J-7. Au-delà, les arrhes restent dues. C'est juste pour bloquer ma disponibilité.",
  },
  {
    q: "Tu abordes tous les sujets ?",
    a: "Pour rester dans un cadre éthique, je n'aborde pas la santé, le décès, ni les questions intrusives sur un tiers. Les prestations relèvent du bien-être et ne remplacent pas un avis médical, psychologique, juridique ou financier.",
  },
  {
    q: "Les mineurs peuvent-ils participer ?",
    a: "Non, les consultations et guidances sont réservées aux personnes majeures, même accompagnées d'un parent.",
  },
];

const CHARTE_ITEMS = [
  { title: "Confidentialité", desc: "Toutes les consultations et guidances sont strictement confidentielles. Les échanges et émotions partagés restent privés." },
  { title: "Respect & bienveillance", desc: "Chaque personne est accueillie avec respect, neutralité et douceur. Aucun jugement n'est porté." },
  { title: "Honnêteté & transparence", desc: "Les guidances sont réalisées avec sincérité, sérieux et clarté. Aucune promesse de résultat n'est faite." },
  { title: "Focalisation sur le/la consultant(e)", desc: "Les tirages se concentrent sur la personne consultante. Les questions intrusives sur un tiers ne sont pas abordées." },
  { title: "Sujets non abordés", desc: "Santé, décès, questions intrusives sur un tiers. Les prestations relèvent du bien-être et ne remplacent pas un avis médical, psychologique, juridique ou financier." },
  { title: "Mineurs", desc: "Réservé aux personnes majeures. Les mineurs ne sont pas autorisés à consulter, même accompagnés d'un parent." },
  { title: "Règlement de participation", desc: "En cas de comportement irrespectueux, agressif, intrusif ou d'état d'ébriété avancé, je me réserve le droit de refuser ou d'interrompre la participation, sans remboursement." },
  { title: "Photos / vidéos", desc: "Uniquement avec l'accord des personnes concernées." },
];


function FaqItem({ q, a, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/10 py-4">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between text-left gap-4 group"
        data-testid={`faq-toggle-${idx}`}
      >
        <span className="text-[#F7F3FC] font-medium text-base md:text-lg group-hover:text-[#D926A9] transition-colors">{q}</span>
        <ChevronDown className={`w-5 h-5 text-[#D926A9] flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <motion.p
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="text-[#B59ED1] leading-relaxed mt-3 text-sm md:text-base"
        >
          {a}
        </motion.p>
      )}
    </div>
  );
}


/**
 * @param {Object} props
 * @param {string} props.slug                ex: "evjf" ou "soiree-privee"
 * @param {string} props.h1                  ex: "EVJF Voyance & Tarot — animation originale"
 * @param {string} props.heroEyebrow         ex: "EVJF / EVJG"
 * @param {string} props.heroSubtitle        ex: "Pour un enterrement de vie de jeune fille (ou garçon) inoubliable"
 * @param {string} props.metaTitle
 * @param {string} props.metaDescription
 * @param {string} props.keywords
 * @param {string} props.heroImage
 * @param {string} props.serviceName         pour JSON-LD
 * @param {string} props.serviceDescription  pour JSON-LD
 * @param {Array}  [props.faqExtra]          questions supplémentaires propres à ce service
 * @param {string} props.ctaSubject          pré-rempli dans le mailto
 */
export default function PrivateEventPage({
  slug, h1, heroEyebrow, heroSubtitle,
  metaTitle, metaDescription, keywords, heroImage,
  serviceName, serviceDescription,
  faqExtra = [], ctaSubject,
}) {
  const url = SITE_URL + "/services/" + slug;
  const faqList = [...faqExtra, ...COMMON_FAQ_BASE];

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "@id": url + "#service",
        "name": serviceName,
        "description": serviceDescription,
        "provider": {
          "@type": "LocalBusiness",
          "name": "Bilan 2 Vie",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Montélimar",
            "postalCode": "26200",
            "addressCountry": "FR",
          },
          "telephone": "+33659452967",
        },
        "areaServed": [
          { "@type": "City", "name": "Montélimar" },
          { "@type": "City", "name": "Valence" },
          { "@type": "City", "name": "Pierrelatte" },
          { "@type": "City", "name": "Bollène" },
          { "@type": "City", "name": "Orange" },
          { "@type": "AdministrativeArea", "name": "Drôme" },
          { "@type": "AdministrativeArea", "name": "Ardèche" },
          { "@type": "AdministrativeArea", "name": "Vaucluse" },
          {
            "@type": "GeoCircle",
            "geoMidpoint": {
              "@type": "GeoCoordinates",
              "latitude": 44.5581,
              "longitude": 4.7493,
            },
            "geoRadius": "50000",
          },
        ],
        "serviceType": "Animation événementielle spirituelle",
        "offers": {
          "@type": "Offer",
          "price": "70",
          "priceCurrency": "EUR",
          "priceSpecification": {
            "@type": "UnitPriceSpecification",
            "price": "70",
            "priceCurrency": "EUR",
            "unitText": "HUR",
          },
          "availability": "https://schema.org/InStock",
          "url": url,
        },
      },
      {
        "@type": "FAQPage",
        "mainEntity": faqList.map((f) => ({
          "@type": "Question",
          "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.a },
        })),
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Accueil", "item": SITE_URL + "/" },
          { "@type": "ListItem", "position": 2, "name": "Prestations", "item": SITE_URL + "/services" },
          { "@type": "ListItem", "position": 3, "name": serviceName, "item": url },
        ],
      },
    ],
  };

  const fullTitle = metaTitle + " | Bilan 2 Vie Montélimar";

  return (
    <main className="pt-24 min-h-screen bg-[#1B132B] text-[#F7F3FC]">
      <Helmet>
        <title>{fullTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={keywords} />
        <link rel="canonical" href={url} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={fullTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={heroImage} />
        <meta property="og:locale" content="fr_FR" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={fullTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={heroImage} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* HERO — split layout : texte à gauche, image bien visible à droite */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#1B132B] via-[#241738] to-[#1B132B]">
        {/* Décor subtil en fond (étoiles, halo) */}
        <div className="absolute inset-0 opacity-30 pointer-events-none" aria-hidden>
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#D926A9]/20 blur-3xl" />
          <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-[#00E5FF]/10 blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Texte */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center lg:text-left order-2 lg:order-1"
            >
              <Badge className="bg-[#D926A9]/15 text-[#D926A9] border border-[#D926A9]/30 mb-6 text-xs uppercase tracking-[0.2em] px-4 py-1.5">
                {heroEyebrow}
              </Badge>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mb-6 leading-tight" data-testid="private-event-h1">
                {h1}
              </h1>
              <p className="text-base md:text-lg text-[#E2D5F8] leading-relaxed">
                {heroSubtitle}
              </p>

              <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start mt-8">
                <Button
                  asChild
                  size="lg"
                  className="bg-[#D926A9] hover:bg-[#B5208E] text-white px-8"
                  data-testid="cta-devis"
                >
                  <a href={`mailto:guillaume@bilan2vie.fr?subject=${encodeURIComponent(ctaSubject)}`}>
                    <Mail className="w-4 h-4 mr-2" /> Demander mon devis
                  </a>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  size="lg"
                  className="border-[#D926A9]/40 text-[#E2D5F8] hover:bg-[#D926A9]/10 hover:text-white px-8"
                >
                  <a href="#prestations">Découvrir les formules</a>
                </Button>
              </div>

              <div className="flex flex-wrap justify-center lg:justify-start gap-6 mt-8 text-sm text-[#B59ED1]">
                <div className="inline-flex items-center gap-2"><Users className="w-4 h-4 text-[#D926A9]" /> Jusqu&apos;à 10 pers.</div>
                <div className="inline-flex items-center gap-2"><Clock className="w-4 h-4 text-[#D926A9]" /> 2 à 3 h</div>
                <div className="inline-flex items-center gap-2"><MapPin className="w-4 h-4 text-[#D926A9]" /> 50 km autour de Montélimar</div>
              </div>
            </motion.div>

            {/* Image — bien visible, encadrée, légère animation flottante */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="order-1 lg:order-2"
            >
              <div className="relative">
                {/* Halo lumineux derrière l'image */}
                <div className="absolute -inset-4 bg-gradient-to-br from-[#D926A9]/30 via-[#3A115E]/40 to-[#00E5FF]/20 rounded-3xl blur-2xl opacity-70" aria-hidden />
                <div className="relative aspect-square w-full max-w-lg mx-auto rounded-3xl overflow-hidden border border-white/15 shadow-2xl shadow-[#D926A9]/20">
                  <img
                    src={heroImage}
                    alt={h1}
                    className="w-full h-full object-cover"
                    loading="eager"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FORMULAS */}
      <section id="prestations" className="py-20 md:py-24 bg-[#1B132B]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="text-[#D926A9] text-xs uppercase tracking-[0.25em] font-medium">3 formules à combiner</span>
            <h2 className="font-serif text-3xl md:text-4xl mt-3">Choisis l&apos;expérience qui te ressemble</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {FORMULAS.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-[#261B3B] border border-white/10 rounded-2xl p-7 hover:border-[#D926A9]/40 transition-all hover:-translate-y-1"
                data-testid={`formula-card-${i}`}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                  style={{ backgroundColor: f.color + "20", color: f.color }}
                >
                  <f.icon className="w-6 h-6" />
                </div>
                <h3 className="font-serif text-xl mb-3">{f.title}</h3>
                <p className="text-[#B59ED1] text-sm leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TARIFS */}
      <section className="py-20 bg-gradient-to-b from-[#1B132B] to-[#0F0820]">
        <div className="max-w-3xl mx-auto px-6">
          <div className="bg-gradient-to-br from-[#D926A9]/15 via-[#3A115E]/30 to-[#00E5FF]/10 border border-[#D926A9]/30 rounded-3xl p-8 md:p-12 text-center backdrop-blur-sm">
            <span className="text-[#D926A9] text-xs uppercase tracking-[0.25em] font-medium">Tarif & capacité</span>
            <div className="flex items-baseline justify-center gap-2 mt-4 mb-3">
              <span className="font-serif text-5xl md:text-6xl text-[#F7F3FC]">70</span>
              <span className="text-[#B59ED1] text-xl">€ / heure</span>
            </div>
            <p className="text-[#E2D5F8] text-base md:text-lg leading-relaxed">
              Jusqu&apos;à <strong>10 personnes</strong>. Durée conseillée : <strong>2 à 3 h</strong> selon le format.
            </p>
            <div className="text-sm text-[#B59ED1] mt-5 space-y-1">
              <p>Au-delà de 10 personnes : on allonge la durée (bar à tirages) ou on ouvre un 2ᵉ créneau — devis personnalisé.</p>
              <p>Frais de déplacement selon lieu (précisés au devis).</p>
            </div>
            <div className="mt-7 pt-6 border-t border-white/10 text-xs text-[#B59ED1] flex flex-wrap justify-center gap-x-6 gap-y-2">
              <span>Arrhes 60€ à la réservation</span>
              <span>•</span>
              <span>Annulation gratuite jusqu&apos;à J-7</span>
            </div>
          </div>
        </div>
      </section>

      {/* INCLUS & LOGISTIQUE */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-10">
          <div className="bg-[#261B3B] border border-white/10 rounded-2xl p-7">
            <div className="inline-flex items-center gap-2 text-[#00E5FF] text-xs uppercase tracking-[0.2em] mb-4">
              <ShieldCheck className="w-4 h-4" /> Matériel fourni
            </div>
            <ul className="space-y-3 text-[#E2D5F8] text-sm">
              {["Objets de décoration","Tapis de tirage et bougies LED 🕯️","Brume énergétique douce 🌫️","Trame personnalisée selon la Lune du moment"].map((item) => (
                <li key={item} className="flex items-start gap-3"><Check className="w-4 h-4 text-[#D926A9] flex-shrink-0 mt-0.5" /><span>{item}</span></li>
              ))}
            </ul>
          </div>
          <div className="bg-[#261B3B] border border-white/10 rounded-2xl p-7">
            <div className="inline-flex items-center gap-2 text-[#D926A9] text-xs uppercase tracking-[0.2em] mb-4">
              <Users className="w-4 h-4" /> De ton côté
            </div>
            <ul className="space-y-3 text-[#E2D5F8] text-sm">
              <li className="flex items-start gap-3"><Check className="w-4 h-4 text-[#D926A9] flex-shrink-0 mt-0.5" /><span>Une table + 2 chaises (pour le Corner)</span></li>
              <li className="flex items-start gap-3"><Check className="w-4 h-4 text-[#D926A9] flex-shrink-0 mt-0.5" /><span>Un espace calme (pour le Cercle de Lune)</span></li>
              <li className="flex items-start gap-3"><Check className="w-4 h-4 text-[#D926A9] flex-shrink-0 mt-0.5" /><span>Pas de fumée persistante, volume maîtrisé</span></li>
              <li className="flex items-start gap-3"><Check className="w-4 h-4 text-[#D926A9] flex-shrink-0 mt-0.5" /><span>Ambiance soignée et bienveillante 🌟</span></li>
            </ul>
          </div>
        </div>
      </section>

      {/* DEROULE */}
      <section className="py-20 bg-[#0F0820]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="text-[#00E5FF] text-xs uppercase tracking-[0.25em] font-medium">Comment ça se passe</span>
            <h2 className="font-serif text-3xl md:text-4xl mt-3">Déroulé type</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {TIMELINE.map((t, i) => (
              <motion.div
                key={t.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D926A9] to-[#3A115E] flex items-center justify-center mx-auto mb-4 font-serif text-lg">
                  {t.step}
                </div>
                <h3 className="font-serif text-xl mb-2">{t.title}</h3>
                <p className="text-[#B59ED1] text-sm leading-relaxed">{t.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* VALEUR AJOUTEE */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="text-[#D926A9] text-xs uppercase tracking-[0.25em] font-medium">Pourquoi me choisir</span>
            <h2 className="font-serif text-3xl md:text-4xl mt-3">Ma valeur ajoutée</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {VALUE_PROPS.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-[#261B3B] border border-white/10 rounded-2xl p-6"
              >
                <v.icon className="w-7 h-7 text-[#D926A9] mb-4" />
                <h3 className="font-serif text-lg mb-2">{v.title}</h3>
                <p className="text-[#B59ED1] text-sm leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-[#0F0820]">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-[#00E5FF] text-xs uppercase tracking-[0.25em] font-medium">Questions fréquentes</span>
            <h2 className="font-serif text-3xl md:text-4xl mt-3">FAQ</h2>
          </div>
          <div className="bg-[#261B3B] rounded-2xl border border-white/10 px-6 py-4">
            {faqList.map((f, i) => <FaqItem key={f.q} q={f.q} a={f.a} idx={i} />)}
          </div>
        </div>
      </section>

      {/* CHARTE */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-[#D926A9] text-xs uppercase tracking-[0.25em] font-medium">Charte éthique</span>
            <h2 className="font-serif text-3xl md:text-4xl mt-3">Mon cadre & mes engagements</h2>
            <p className="text-[#B59ED1] mt-4 text-sm md:text-base max-w-2xl mx-auto">
              Mes prestations sont proposées dans un cadre bienveillant, éthique et respectueux. Mon intention : vous faire vivre un moment lumineux, profond et sécurisant, dans une ambiance conviviale, sans jugement. 🌙✨
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {CHARTE_ITEMS.map((c) => (
              <div key={c.title} className="bg-[#261B3B] border border-white/10 rounded-xl p-5">
                <h4 className="font-medium text-[#F7F3FC] mb-2 text-sm uppercase tracking-wider">{c.title}</h4>
                <p className="text-[#B59ED1] text-sm leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 bg-gradient-to-br from-[#3A115E] via-[#1B132B] to-[#D926A9]/20">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-serif text-3xl md:text-5xl mb-5">Un format signature pour ton évènement ?</h2>
          <p className="text-[#E2D5F8] text-base md:text-lg mb-10 leading-relaxed">
            Donne-moi la date, le lieu et l&apos;ambiance souhaitée : je te prépare une proposition calibrée, claire et lumineuse. ✨
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-[#D926A9] hover:bg-[#B5208E] text-white px-10 py-6 text-base">
              <a href={`mailto:guillaume@bilan2vie.fr?subject=${encodeURIComponent(ctaSubject)}`} data-testid="cta-final-devis">
                <Mail className="w-4 h-4 mr-2" /> Demander mon devis personnalisé
              </a>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-[#00E5FF]/40 text-[#E2D5F8] hover:bg-[#00E5FF]/10 hover:text-white px-10 py-6 text-base">
              <Link to="/booking"><Calendar className="w-4 h-4 mr-2" /> Voir mes autres prestations</Link>
            </Button>
          </div>
        </div>
      </section>
    </main>
  );
}
